import { useEffect } from "react";
import { useWebSocket } from "contexts/WebSocketContext";
import { useAuth } from "contexts/AuthContext";
import { useStateContext } from "contexts/ContextProvider";

/**
 * Hook to listen for entity updates and trigger refetch + notification
 * Generic hook for all entity types (Client, Linguist, Task, Invoice, etc.)
 * @param {Object} params
 * @param {string} params.entityType - Type of entity (e.g., "Client", "Linguist", "Task", "Invoice")
 * @param {number} params.entityId - ID of the entity
 * @param {Function} params.onUpdate - Callback function to refetch data (called when update is received, receives updatedBy object)
 * @param {Function} params.toastFn - Toast notification function (optional, for custom toast handling)
 * @returns {void}
 */
export const useEntityUpdates = ({
  entityType,
  entityId,
  onUpdate,
  toastFn,
}) => {
  const { socket } = useWebSocket();
  const { currentUser } = useAuth();
  const { showNotification } = useStateContext();

  useEffect(() => {
    if (!socket || !entityType || !entityId || !onUpdate) {
      return;
    }

    // Handler for entity updates
    const handleEntityUpdate = ({
      updatedBy,
      entityType: updateEntityType,
      modelType: updateModelType,
      recordId: updateRecordId,
      changeType,
      changeAction,
    }) => {
      // Accept both entityType (from presence-based updates) and modelType (from socket broadcasts)
      const incomingType = updateEntityType || updateModelType;
      const recordIdMatch = updateRecordId === entityId;
      const recordIdNumberMatch = Number(updateRecordId) === Number(entityId);

      // Only process if this is for our current entity (accept both entityType and modelType)
      if (
        incomingType === entityType &&
        (recordIdMatch || recordIdNumberMatch)
      ) {
        // Don't show notification to the user who made the update
        if (updatedBy.userId !== currentUser?.id) {
          // Show toast notification
          if (toastFn) {
            toastFn(
              `${updatedBy.userName} updated the record. Check History for more details.`,
            );
          } else if (showNotification) {
            showNotification({
              type: "info",
              message: `${updatedBy.userName} updated the record. Check History for more details.`,
            });
          }

          // Trigger refetch with updater info + change metadata
          onUpdate(updatedBy, {
            changeType,
            changeAction,
            modelType: incomingType,
            recordId: updateRecordId,
          });
        }
      }
    };

    // Listen for entity:updated events
    socket.on("entity:updated", handleEntityUpdate);

    // Cleanup
    return () => {
      socket.off("entity:updated", handleEntityUpdate);
    };
  }, [
    socket,
    entityType,
    entityId,
    onUpdate,
    currentUser?.id,
    showNotification,
    toastFn,
  ]);
};

export default useEntityUpdates;
